import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/api.service";
import { updateTenantSubdomain } from "../utils/axiosConfig";
import logger from "../utils/logger";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const result = await authService.autoLogin();
        if (mounted && result.success) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        logger.error("Auth check failed:", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError("");

    try {
      const response = await authService.login(
        {
          email: username,
          password,
        },
        rememberMe
      );

      logger.debug("Login response:", response);

      if (response.success && response.data.token) {
        // تخزين التوكن وبيانات المستخدم
        const { token, user, subscription, tenant } = response.data;

        // ✅ حفظ علامة أن المستخدم قد سجل الدخول سابقاً (للتوجيه الذكي في صفحة الهبوط)
        localStorage.setItem('hasLoggedInBefore', 'true');

        // ✅ حفظ معلومات tenant وتحديث axios subdomain
        if (tenant && tenant.subdomain) {
          localStorage.setItem("tenantInfo", JSON.stringify(tenant));
          updateTenantSubdomain(tenant.subdomain);
          logger.info("✅ Tenant subdomain set to:", tenant.subdomain);
        }

        // ✅ حفظ معلومات الاشتراك
        if (subscription) {
          localStorage.setItem("subscription", JSON.stringify(subscription));
        }

        if (rememberMe) {
          localStorage.setItem("token", token);
          localStorage.setItem("user", JSON.stringify(user));
        } else {
          sessionStorage.setItem("token", token);
          sessionStorage.setItem("user", JSON.stringify(user));
        }

        // ✅ التحقق من حالة الاشتراك
        logger.debug("🔍 Subscription check:", subscription);

        // التحقق من حالة الاشتراك بشكل صحيح
        if (
          !subscription ||
          !subscription.isActive ||
          subscription.requiresAction
        ) {
          // حفظ معلومات الاشتراك المنتهي أو في انتظار الدفع
          localStorage.setItem("subscriptionExpired", "true");
          if (subscription) {
            localStorage.setItem(
              "subscriptionStatus",
              JSON.stringify(subscription)
            );
          }

          // توجيه لصفحة انتهاء الاشتراك
          logger.warn(
            "⛔ Subscription not active or requires action, redirecting to /subscription-expired"
          );
          window.location.href = "/subscription-expired";
        } else {
          // التوجيه مباشرة إلى لوحة التحكم
          logger.info("✅ Subscription active, redirecting to /dashboard");
          window.location.href = "/dashboard";
        }
      } else {
        setError("بيانات الدخول غير صحيحة");
      }
    } catch (error) {
      // التحقق من الخطأ لتحديد ما إذا كان بسبب اشتراك غير نشط
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        const errorData = error.response.data;

        // إذا كان الخطأ يتعلق باشتراك غير نشط، قم بتوجيه المستخدم للصفحة المناسبة
        if (errorData.requiresAction) {
          // حفظ معلومات الاشتراك
          if (errorData.subscription) {
            localStorage.setItem(
              "subscription",
              JSON.stringify(errorData.subscription)
            );
          }

          // ✅ تحديث axios subdomain من معلومات المستأجر في حالة الخطأ
          if (errorData.tenant && errorData.tenant.subdomain) {
            localStorage.setItem("tenantInfo", JSON.stringify(errorData.tenant));
            updateTenantSubdomain(errorData.tenant.subdomain);
            logger.info("✅ Tenant subdomain set to:", errorData.tenant.subdomain);
          }

          // عرض رسالة للمستخدم بناءً على نوع الإجراء المطلوب
          switch (errorData.requiresAction) {
            case "payment":
              setError("يجب إكمال عملية الدفع لتفعيل الاشتراك");
              // الانتقال إلى صفحة الدفع بعد فترة قصيرة
              setTimeout(() => {
                window.location.href = "/payment-pending";
              }, 3000);
              return;
            case "subscribe":
              setError("انتهت صلاحية الاشتراك، يجب التجديد");
              // الانتقال إلى صفحة الاشتراكات بعد فترة قصيرة
              setTimeout(() => {
                window.location.href = "/subscription-expired";
              }, 3000);
              return;
            case "renew":
              setError("انتهت صلاحية الاشتراك، يجب التجديد");
              // الانتقال إلى صفحة تجديد الاشتراك بعد فترة قصيرة
              setTimeout(() => {
                window.location.href = "/subscription-expired";
              }, 3000);
              return;
            case "contact_support":
              setError("تم تعليق الحساب، يرجى التواصل مع الدعم الفني");
              // الانتقال إلى صفحة الاتصال بالدعم بعد فترة قصيرة
              setTimeout(() => {
                window.location.href = "/contact-support";
              }, 3000);
              return;
            default:
              setError(errorData.message || "لا يمكن تسجيل الدخول. اشتراكك غير نشط.");
              // الانتقال إلى صفحة انتهاء الاشتراك بعد فترة قصيرة
              setTimeout(() => {
                window.location.href = "/subscription-expired";
              }, 3000);
              return;
          }
        } else {
          // عرض الرسالة من الخادم إذا كانت متوفرة
          setError(errorData.message || "البريد الإلكتروني أو كلمة المرور غير صحيحة");
        }
      } else {
        setError(error.message || "البريد الإلكتروني أو كلمة المرور غير صحيحة");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        جاري التحميل...
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-100 flex items-center justify-center"
      dir="rtl"
    >
      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        <h2 className="text-2xl font-bold mb-6 text-center">تسجيل الدخول</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              البريد الإلكتروني
            </label>
            <input
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              كلمة المرور
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="ml-2"
              />
              <span className="text-sm text-gray-700">تذكرني</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
          >
            {loading ? "جاري التحميل..." : "تسجيل الدخول"}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            ليس لديك حساب؟{" "}
            <button
              type="button"
              onClick={() => {
                logger.debug("Navigating to register");
                navigate("/register");
              }}
              className="text-blue-500 hover:text-blue-700 font-medium focus:outline-none"
            >
              تسجيل حساب جديد
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;